import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
    Tag, 
    Star, 
    Clock, 
    Package, 
    ChevronLeft, 
    ChevronRight,
    X,
    ZoomIn
} from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PromoFeaturedBanner = ({ onFilterPromo }) => {
    const navigate = useNavigate();
    const [promos, setPromos] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [dismissed, setDismissed] = useState({});

    useEffect(() => {
        const fetchPromos = async () => {
            try {
                const response = await axios.get(`${API_URL}/promos/active`);
                // Only show featured promos
                const featuredPromos = (response.data.promos || [])
                    .filter(p => p.display_mode === 'featured' && p.available_count > 0);
                setPromos(featuredPromos);
            } catch (error) {
                console.error('Fetch promos error:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPromos();
    }, []);

    const currentPromo = promos[currentIndex];

    const nextPromo = () => {
        setCurrentIndex((prev) => (prev + 1) % promos.length);
    };

    const prevPromo = () => {
        setCurrentIndex((prev) => (prev - 1 + promos.length) % promos.length);
    };

    const dismissPromo = (promoId) => {
        setDismissed(prev => ({ ...prev, [promoId]: true }));
    };

    const openPhotoModal = (photo) => {
        setSelectedPhoto(photo);
        setPhotoModalOpen(true);
    };

    const getTimeRemaining = (endDate) => {
        if (!endDate) return null;
        
        const now = new Date();
        const end = new Date(endDate);
        const diff = end - now;
        
        if (diff <= 0) return 'Terminée';
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (days > 0) return `${days}j ${hours}h`;
        return `${hours}h`;
    };

    // Filter out dismissed promos
    const visiblePromos = promos.filter(p => !dismissed[p.id]);

    if (loading || visiblePromos.length === 0) return null;

    const promo = visiblePromos[currentIndex % visiblePromos.length];
    if (!promo) return null;

    const timeRemaining = getTimeRemaining(promo.end_date);

    // Sort pricing tiers
    const sortedTiers = Object.entries(promo.pricing_tiers || {})
        .map(([qty, price]) => ({ qty: parseInt(qty), price }))
        .sort((a, b) => a.qty - b.qty);

    return (
        <>
            <div className="mb-6 rounded-xl overflow-hidden bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white shadow-lg relative">
                {/* Dismiss button */}
                <button
                    onClick={() => dismissPromo(promo.id)}
                    className="absolute top-3 right-3 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors z-10"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                        {/* Left: Info */}
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                                <Star className="w-6 h-6 text-yellow-300 fill-yellow-300" />
                                <h2 className="text-2xl font-bold">{promo.name}</h2>
                                {timeRemaining && (
                                    <Badge className="bg-white/20 text-white border-0">
                                        <Clock className="w-3 h-3 mr-1" />
                                        Fin dans {timeRemaining}
                                    </Badge>
                                )}
                            </div>

                            {promo.description && (
                                <p className="text-white/90 mb-4">{promo.description}</p>
                            )}

                            {/* Pricing Tiers */}
                            <div className="flex flex-wrap gap-3 mb-4">
                                {sortedTiers.map(({ qty, price }, index) => (
                                    <div 
                                        key={qty}
                                        className={`px-4 py-2 rounded-lg ${
                                            index === 0 
                                                ? 'bg-white/20' 
                                                : index === sortedTiers.length - 1 
                                                    ? 'bg-green-600 ring-2 ring-white' 
                                                    : 'bg-white/30'
                                        }`}
                                    >
                                        <p className="text-xs opacity-90">
                                            {qty === 1 ? 'Prix unitaire' : `≥ ${qty} pièces`}
                                        </p>
                                        <p className="text-xl font-bold">{price}€</p>
                                        {qty > 1 && (
                                            <p className="text-xs opacity-75">/pièce</p>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <Package className="w-5 h-5" />
                                    <span className="font-medium">{promo.available_count} produits disponibles</span>
                                </div>
                                <Button 
                                    onClick={() => onFilterPromo?.(promo.id)}
                                    className="bg-white text-orange-600 hover:bg-white/90"
                                >
                                    Voir les produits
                                </Button>
                            </div>
                        </div>

                        {/* Right: Photos */}
                        {promo.photos && promo.photos.length > 0 && (
                            <div className="lg:w-80">
                                <div className="grid grid-cols-3 gap-2">
                                    {promo.photos.slice(0, 6).map((photo, index) => (
                                        <div 
                                            key={index}
                                            className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                                            onClick={() => openPhotoModal(photo)}
                                        >
                                            <img
                                                src={photo}
                                                alt={`Produit ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation arrows for multiple promos */}
                {visiblePromos.length > 1 && (
                    <>
                        <button
                            onClick={prevPromo}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={nextPromo}
                            className="absolute right-10 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                        {/* Dots */}
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                            {visiblePromos.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentIndex(i)}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                        i === currentIndex % visiblePromos.length 
                                            ? 'bg-white' 
                                            : 'bg-white/40'
                                    }`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Photo Modal */}
            <Dialog open={photoModalOpen} onOpenChange={setPhotoModalOpen}>
                <DialogContent className="max-w-3xl p-0 overflow-hidden">
                    {selectedPhoto && (
                        <img
                            src={selectedPhoto}
                            alt="Photo produit"
                            className="w-full h-auto"
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

// Badge component for product cards
export const PromoBadge = ({ promo }) => {
    if (!promo) return null;

    return (
        <div className="absolute top-2 left-2 z-10">
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-md">
                <Tag className="w-3 h-3 mr-1" />
                PROMO
            </Badge>
        </div>
    );
};

export default PromoFeaturedBanner;
